import {
  AfterViewInit,
  Directive,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';

/**
 * Clamp comment text to 4 visual lines (wrapping and explicit breaks)
 * and report whether the full text overflows that box.
 */
@Directive({
  selector: '[wallCommentClamp]',
})
export class WallCommentClampDirective implements AfterViewInit, OnChanges, OnDestroy {
  @Input() wallCommentClamp = true;
  @Output() wallCommentOverflow = new EventEmitter<boolean>();

  private overflow: boolean | null = null;
  private resizeObs: ResizeObserver | null = null;
  private mutationObs: MutationObserver | null = null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private ngZone: NgZone
  ) {}

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    this.applyClampClass();
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObs = new ResizeObserver(() => this.queueMeasure());
      this.resizeObs.observe(el);
    }
    if (typeof MutationObserver !== 'undefined') {
      this.mutationObs = new MutationObserver(() => this.queueMeasure());
      this.mutationObs.observe(el, {
        characterData: true,
        childList: true,
        subtree: true,
      });
    }
    this.queueMeasure();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['wallCommentClamp']) {
      this.applyClampClass();
      this.queueMeasure();
    }
  }

  ngOnDestroy(): void {
    this.resizeObs?.disconnect();
    this.mutationObs?.disconnect();
  }

  private applyClampClass(): void {
    this.host.nativeElement.classList.toggle('is-clamped', !!this.wallCommentClamp);
  }

  private queueMeasure(): void {
    this.ngZone.runOutsideAngular(() => {
      requestAnimationFrame(() => this.measure());
    });
  }

  private measure(): void {
    const el = this.host.nativeElement;
    if (!this.wallCommentClamp) {
      this.emitOverflow(false);
      return;
    }
    const overflowing = el.scrollHeight - el.clientHeight > 2;
    this.emitOverflow(overflowing);
  }

  private emitOverflow(next: boolean): void {
    if (this.overflow === next) {
      return;
    }
    this.overflow = next;
    this.ngZone.run(() => this.wallCommentOverflow.emit(next));
  }
}
